import { useMutation, type ApolloError } from '@apollo/client'
import { Add, Delete, Edit } from '@mui/icons-material'
import { Button, Stack } from '@mui/material'
import { useRef, useState, type FC } from 'react'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import { BulkActionContextProvider } from 'recruiter/contexts/BulkActionContext'
import { useCandidateSqlSearch } from 'recruiter/hooks'
import SendToSearch from 'recruiter/pages/Research/SendToSearch'
import ConfirmDialog from 'shared/components/DocumentUpload/ConfirmDialog'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError } from 'shared/utils'
import { RemoveRosterCandidatesByCandidateIds } from '../BulkActions/BulkActions.gql'
import EditColors from '../BulkActions/EditColors'
import EditProcessStatus from '../BulkActions/EditProcessStatus'
import { useRosterSearch } from './columns/RosterSearchContext'
import { RosterSearchAddToRoster } from './RosterSearchAddToRoster'
import { RosterSearchMoreActions } from './RosterSearchMoreActions'

export const RosterSearchBulkActions: FC = () => {
    const { rosterId, canEdit, viewMode } = useRosterSearch()
    const { selectedCandidateIds, setSelectedCandidateIds, data } =
        useCandidateSqlSearch()
    const toast = useToast()

    const selectedRosterCandidateIds = data
        .filter((row) => selectedCandidateIds.includes(row.candidateId))
        .map((row) => row.rosterCandidateId)
        .filter((id): id is string => !!id)

    const [confirmOpen, setConfirmOpen] = useState(false)
    const [addToRosterOpen, setAddToRosterOpen] = useState(false)
    const [colorsOpen, setColorsOpen] = useState(false)
    const [processStatusOpen, setProcessStatusOpen] = useState(false)
    const addButtonRef = useRef<HTMLButtonElement>(null)
    const colorsButtonRef = useRef<HTMLButtonElement>(null)
    const processStatusButtonRef = useRef<HTMLButtonElement>(null)

    const [removeFromRoster, { loading }] = useMutation(
        RemoveRosterCandidatesByCandidateIds,
        {
            refetchQueries: [GetCandidatesForRosterSql],
        }
    )

    const handleRemove = async () => {
        try {
            const { data } = await removeFromRoster({
                variables: {
                    rosterId,
                    candidateIds: selectedCandidateIds,
                },
            })
            if (data?.removeCandidatesFromRosterByCandidateIds.success) {
                toast({
                    severity: 'success',
                    title: 'Success',
                    message:
                        data.removeCandidatesFromRosterByCandidateIds.message ||
                        'Candidates removed successfully',
                })
                setSelectedCandidateIds([])
            } else {
                toast({
                    severity: 'error',
                    title: 'Error',
                    message:
                        data?.removeCandidatesFromRosterByCandidateIds
                            .message || 'Failed to remove candidates',
                })
            }
        } catch (error) {
            toast({
                severity: 'error',
                title: 'Error',
                message: formatGraphQLError(
                    error as ApolloError,
                    'Something went wrong.'
                ),
            })
        }
        setConfirmOpen(false)
    }

    const buttonSx = {
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        fontWeight: 500,
        py: 0.5,
        px: 1.5,
        borderColor: 'divider',
        color: 'text.secondary',
        '&:hover': {
            borderColor: 'text.secondary',
            bgcolor: 'action.hover',
        },
        '&.Mui-disabled': {
            borderColor: 'divider',
            color: 'text.disabled',
        },
    }

    return (
        <>
            <Stack direction="row" gap={1}>
                {canEdit && viewMode === 'on' && (
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={selectedCandidateIds.length === 0 || loading}
                        onClick={() => setConfirmOpen(true)}
                        sx={buttonSx}
                    >
                        <Delete sx={{ fontSize: '1rem', mr: 0.5 }} />
                        Remove
                    </Button>
                )}
                {canEdit && viewMode === 'all' && (
                    <Button
                        ref={addButtonRef}
                        variant="outlined"
                        size="small"
                        disabled={selectedCandidateIds.length === 0}
                        onClick={() => setAddToRosterOpen(true)}
                        sx={buttonSx}
                    >
                        <Add sx={{ fontSize: '1rem', mr: 0.5 }} />
                        Add
                    </Button>
                )}
                <SendToSearch
                    buttonVariant="outlined"
                    selectedCandidates={selectedCandidateIds}
                />
                {canEdit && (
                    <Button
                        ref={colorsButtonRef}
                        variant="outlined"
                        size="small"
                        disabled={selectedCandidateIds.length === 0}
                        onClick={() => setColorsOpen(true)}
                        sx={buttonSx}
                    >
                        <Edit sx={{ fontSize: '1rem', mr: 0.5 }} />
                        Colors
                    </Button>
                )}
                {canEdit && (
                    <Button
                        ref={processStatusButtonRef}
                        variant="outlined"
                        size="small"
                        disabled={selectedCandidateIds.length === 0}
                        onClick={() => setProcessStatusOpen(true)}
                        sx={buttonSx}
                    >
                        <Edit sx={{ fontSize: '1rem', mr: 0.5 }} />
                        Process Status
                    </Button>
                )}
                <RosterSearchMoreActions
                    selectedCandidateIds={selectedCandidateIds}
                    selectedRosterCandidateIds={selectedRosterCandidateIds}
                    rosterId={rosterId}
                    canEdit={canEdit}
                    viewMode={viewMode}
                />
            </Stack>

            <ConfirmDialog
                open={confirmOpen}
                handleClose={() => setConfirmOpen(false)}
                handleConfirm={handleRemove}
                message={`Are you sure you want to remove ${selectedCandidateIds.length} candidate${selectedCandidateIds.length === 1 ? '' : 's'} from this search?`}
            />

            <RosterSearchAddToRoster
                selectedCandidateIds={selectedCandidateIds}
                rosterId={rosterId}
                isOpen={addToRosterOpen}
                anchorEl={addButtonRef.current}
                onClose={() => setAddToRosterOpen(false)}
                onSuccess={() => setSelectedCandidateIds([])}
            />

            <BulkActionContextProvider>
                {colorsOpen && (
                    <EditColors
                        onClose={() => setColorsOpen(false)}
                        selectedRosterCandidateIds={selectedRosterCandidateIds}
                        isOpen={colorsOpen}
                        anchorRef={colorsButtonRef}
                        rosterId={rosterId}
                        candidateIds={selectedCandidateIds}
                    />
                )}

                {processStatusOpen && (
                    <EditProcessStatus
                        rosterId={rosterId}
                        onClose={() => setProcessStatusOpen(false)}
                        selectedCandidateIds={selectedCandidateIds}
                        selectedRosterCandidateIds={selectedRosterCandidateIds}
                        isOpen={processStatusOpen}
                        anchorRef={processStatusButtonRef}
                    />
                )}
            </BulkActionContextProvider>
        </>
    )
}
